import { Box, Flex, HStack, Icon, Image, Text, useToast } from '@chakra-ui/react';
import { ImagePlus } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useMemo } from 'react';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import { UserAvatar } from '@duke-hq/ui/components/UserAvatar/UserAvatar';
import { ContributionSection } from './ContributionSection';

type CauseContributionProps = {
  cause: RouterOutputs['groupRewards']['listCauses'][number];
  onClose: () => void;
};

type ContributionHeaderProps = {
  imageUrl: string;
  name: string;
  targetAmount: number;
  suggestedBy: {
    first_name?: string | null;
    last_name?: string | null;
    picture_url?: string | null;
  } | null;
  contributed?: number;
  totalContributors?: number;
  contributions: RouterOutputs['groupRewards']['getCauseContributions'];
};

function ContributionHeader({
  imageUrl,
  name,
  targetAmount,
  suggestedBy,
  contributions,
}: ContributionHeaderProps) {
  const totalContributed = useMemo(() => {
    return contributions.reduce((acc, curr) => acc + (curr.pointsAllocated || 0), 0);
  }, [contributions]);

  return (
    <Box bg="bg.muted" p={6} borderRadius="sm">
      <Flex direction={{ base: 'column', md: 'row' }} align="center" justify="center" gap={8}>
        <Flex
          w={{ base: '100%', md: '250px' }}
          h={{ base: '140px', md: '160px' }}
          borderRadius="md"
          bg="gray.300"
          align="center"
          justify="center"
          overflow="hidden"
          flexShrink={0}
        >
          {imageUrl ? (
            <Image src={imageUrl} alt={name} objectFit="cover" w="100%" h="100%" />
          ) : (
            <Icon as={ImagePlus} boxSize={8} color="fg.muted" />
          )}
        </Flex>

        <Flex direction="column" flex="1" gap={1} textAlign={{ base: 'center', md: 'left' }}>
          <Text fontSize="md" fontWeight="semibold">
            {name}
          </Text>

          <HStack spacing={2} justify={{ base: 'center', md: 'flex-start' }}>
            <Text fontSize="md" fontWeight="semibold" color="fg.muted">
              <Trans>Goal:</Trans>
            </Text>
            <Text fontSize="md" fontWeight="bold">
              ${targetAmount}
            </Text>
          </HStack>

          <HStack spacing={2} justify={{ base: 'center', md: 'flex-start' }} align="center">
            <Text fontSize="xs" color="fg.muted">
              <Trans>Suggested by</Trans>
            </Text>
            <UserAvatar
              user={{
                first_name: suggestedBy?.first_name ?? '',
                last_name: suggestedBy?.last_name ?? '',
                picture_url: suggestedBy?.picture_url ?? null,
              }}
              width="16px"
              height="16px"
              boxSize="4"
              fontSize="2xs"
            />
            <Text fontSize="xs" fontWeight="semibold">
              {suggestedBy != null
                ? [suggestedBy.first_name ?? '', suggestedBy.last_name ?? '']
                    .filter(Boolean)
                    .join(' ')
                : ''}
            </Text>
          </HStack>

          <HStack spacing={2} justify={{ base: 'center', md: 'flex-start' }}>
            <Text fontSize="md" fontWeight="semibold" color="fg.muted">
              <Trans>Contributed</Trans>
            </Text>
            <Text fontSize="md" fontWeight="bold">
              {totalContributed ?? 0}/{targetAmount}
            </Text>
          </HStack>
        </Flex>
      </Flex>
    </Box>
  );
}

function ContributorsList({
  contributions,
}: {
  contributions?: RouterOutputs['groupRewards']['getCauseContributions'];
}) {
  const contributorTotals = useMemo(() => {
    if (!Array.isArray(contributions) || contributions.length === 0) {
      return {};
    }

    // Use a reduce function for a cleaner, single-pass aggregation.
    return contributions.reduce(
      (totalsMap: Record<string, { userName: string; totalPoints: number }>, contribution) => {
        const userKey = contribution.allocatedBy.id;
        const userName = `${contribution.allocatedBy.first_name} ${contribution.allocatedBy.last_name}`;
        const pointsAllocated = contribution.pointsAllocated;
        const existingEntry = totalsMap[userKey];

        return {
          ...totalsMap,
          [userKey]: {
            userName,
            totalPoints: existingEntry
              ? existingEntry.totalPoints + pointsAllocated
              : pointsAllocated,
          },
        };
      },
      {},
    );
  }, [contributions]);

  const contributorElements = Object.entries(contributorTotals).map(([userKey, contributor]) => (
    <Flex key={userKey} justify="space-between" align="center">
      <Text fontSize="sm" color="fg.muted">
        {contributor.userName}
      </Text>
      <Text fontSize="sm" color="fg.muted">
        ${contributor.totalPoints}
      </Text>
    </Flex>
  ));

  return (
    <Flex flexDirection="column">
      <Text color="fg.muted" fontWeight="bold" mb={2}>
        <Trans>Contributors</Trans>
      </Text>
      <Flex
        flexDirection="column"
        bg="gray.100"
        borderRadius="sm"
        p={4}
        gap={2}
        maxHeight="250px"
        overflowY="auto"
      >
        {contributorElements.length > 0 ? (
          contributorElements
        ) : (
          <Text fontSize="sm" color="fg.muted">
            <Trans>No contributions yet.</Trans>
          </Text>
        )}
      </Flex>
    </Flex>
  );
}

export function AllocatePointsModal({ cause, onClose }: CauseContributionProps) {
  const toast = useToast();
  const utils = trpcTenant.useUtils();

  const { data: userPoints } = trpcTenant.groupRewards.getUserIncentivePoints.useQuery();
  const { data: pointsAllocated } = trpcTenant.groupRewards.getUserPointsAllocation.useQuery();
  const { data: causeContributions } = trpcTenant.groupRewards.getCauseContributions.useQuery({
    causeId: cause.id,
  });

  const allocateUserPoints = trpcTenant.groupRewards.allocateUserPoints.useMutation({
    onSuccess: () => {
      toast({
        title: t`Success`,
        description: t`Points allocated successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
    },
    onError: (error) => {
      toast({
        title: error?.message ?? t`Failed to allocate points.`,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
    async onSettled() {
      await Promise.all([
        utils.groupRewards.getUserIncentivePoints.refetch(),
        utils.groupRewards.getUserPointsAllocation.refetch(),
        utils.groupRewards.getCauseContributions.refetch(),
        utils.groupRewards.getRecentContributions.refetch(),
        utils.groupRewards.listCauses.refetch(),
        utils.groupRewards.listContributorsLeaderboardWithInfiniteScroll.refetch(),
        utils.groupRewards.listSiteLeaderboardWithInfiniteScroll.refetch(),
      ]);
    },
  });

  const userPointsAllocated = useMemo(() => {
    const userTotalPoints = userPoints?.allTotalPoints ?? 0;
    const userPointsAllocated = pointsAllocated?._sum?.pointsAllocated ?? 0;

    return userTotalPoints - userPointsAllocated;
  }, [pointsAllocated, userPoints?.allTotalPoints]);

  const onContribute = async (amount: number) => {
    await allocateUserPoints.mutateAsync({
      causeId: cause.id,
      pointsAllocated: amount,
    });
  };

  return (
    <Box bg="bg.canvas" p={4}>
      <ContributionHeader
        imageUrl={typeof cause.imageUrl === 'string' ? cause.imageUrl : ''}
        name={typeof cause.name === 'string' ? cause.name : ''}
        targetAmount={typeof cause.targetAmount === 'number' ? cause.targetAmount : 0}
        suggestedBy={typeof cause.suggestedBy === 'object' ? cause.suggestedBy : null}
        contributions={causeContributions ?? []}
      />
      <ContributionSection pointsAvailable={userPointsAllocated ?? 0} onContribute={onContribute} />
      <ContributorsList contributions={causeContributions} />
    </Box>
  );
}
